/**
 * Represents the prefix for an error message.
 */
// add all letter on the alphabet
type ErrorPref = string;

/**
 * Represents an error number.
 */
type ErrorNumber = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 0;

/**
 * Represents an error level.
 * 1 - Error
 * 2 - Warning
 * 3 - Info
 */
type ErrorLevel = 1 | 2 | 3;

/**
 * Represents an error code in the format of `${ErrorPref}-${ErrorNumber}`.
 */
type ErrorCode =
  `${ErrorPref}-${ErrorLevel}${ErrorNumber}${ErrorNumber}${ErrorNumber}`;

/**
 * Represents a trace of failures.
 */
type ErrorTraceKeys = 'file' | 'class' | 'method' | 'line' | 'function';
type ErrorTrace = {
  [key in ErrorTraceKeys]?: unknown;
};

/**
 * Represents additional metadata associated with an error.
 */
type ErrorMetadata = Record<string, unknown>;

export class FantazFailure implements Error {
  className: string;
  /**
   * The error code associated with the failure.
   */
  code: ErrorCode;

  /**
   * The error trace.
   */
  trace?: ErrorTrace;

  /**
   * The error metadata.
   */
  metadata?: ErrorMetadata;

  /**
   * Represents the error that occurred.
   */
  error: Error;

  /**
   * The name of the error group.
   */
  errorGroup?: string;

  constructor(
    className: string,
    code: ErrorCode,
    error: Error,
    options?: {
      errorGroup?: string;
      trace?: ErrorTrace;
      metadata?: ErrorMetadata;
    },
  ) {
    this.className = className;
    this.code = code;
    this.error = error;
    this.metadata = options?.metadata;
    this.trace = options?.trace;
    this.errorGroup = options?.errorGroup;
  }

  /**
   * Gets the key of the failure. This is the name of the class without the
   * `Failure` suffix and in camel case.
   * @example `UsernameInvalidFailure` -> `usernameInvalid`
   * @example `UsernameDuplicateFailure` -> `usernameDuplicate`
   */
  get key(): string {
    return this.className
      .replace('Failure', '')
      .replace(/\s(.)/g, function (a) {
        return a.toUpperCase();
      })
      .replace(/\s/g, '')
      .replace(/^(.)/, function (b) {
        return b.toLowerCase();
      });
  }

  get name(): string {
    return this.error.name;
  }

  get message(): string {
    return `${this.error.message}`;
  }

  get stack() {
    return this.error.stack;
  }

  toString(): string {
    const trace = this.trace ? JSON.stringify(this.trace) : '';
    const meta = this.metadata ? JSON.stringify(this.metadata) : '';
    return `${this.code}: ${this.message} ${meta} ${trace}`;
  }
}
